import type { Dispatch } from "react";
import { useCallback, useEffect } from "react";

import Sentry from "@similarweb/sentry-logger";

import { swSettings } from "app/common/services/swSettings";
import { enrichCampaignsWithIcons } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsContextUtils";
import {
    getIsRTCreateCampaignsAvailable,
    getMyUserConsumptionMetrics,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsInfraUtils";
import type {
    ICampaignsConsumptionMetrics,
    IKeywordsCampaignData,
    IRTCurrentCampaignData,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import type { RTContextActions } from "app/pages/digital-marketing/keywords-campaigns/infra/RTContext.consts";
import { ERTContextReducerTypes } from "app/pages/digital-marketing/keywords-campaigns/infra/RTContext.consts";
import { useCampaignsConsumptionQuery } from "app/pages/digital-marketing/keywords-campaigns/shared/hooks/data";

export const useCampaignsConsumption = (
    dispatch: Dispatch<RTContextActions>,
    campaignsConfigurations: IKeywordsCampaignData[],
    campaignsConsumptionMetrics: ICampaignsConsumptionMetrics,
) => {
    const hasConsumptionMetrics = !!campaignsConsumptionMetrics?.usedCampaigns;

    const { data: campaignsConsumption, refetch: updateCampaignsConsumptionFromServer } = useCampaignsConsumptionQuery({
        enabled: getIsRTCreateCampaignsAvailable() && !hasConsumptionMetrics,
    });

    useEffect(() => {
        if (campaignsConsumption) {
            dispatch({
                type: ERTContextReducerTypes.UpdateCampaignsConsumptionMetrics,
                payload: { campaignsConsumptionMetrics: campaignsConsumption },
            });
        }
    }, [campaignsConsumption, dispatch]);

    const updateLocalCampaignsAfterCreation = useCallback(
        async (newCampaign: IRTCurrentCampaignData) => {
            try {
                const campaignWithIcons = await enrichCampaignsWithIcons<IRTCurrentCampaignData>([newCampaign]);
                const campaignToAdd: IKeywordsCampaignData = {
                    ...campaignWithIcons[0],
                    createdBy: swSettings.user?.firstname,
                    isShared: false,
                    isDemo: false,
                    isEditable: true, // allowed to owner to delete campaign within first 24 hours
                };
                const allCampaignsToSet = [...campaignsConfigurations, campaignToAdd];

                dispatch({
                    type: ERTContextReducerTypes.UpdateCampaignsConfigurations,
                    payload: {
                        campaignsConfigurations: allCampaignsToSet,
                    },
                });

                const consumptionResults = getMyUserConsumptionMetrics(allCampaignsToSet);

                dispatch({
                    type: ERTContextReducerTypes.UpdateMyUserConsumptionMetrics,
                    payload: { myUserConsumptionMetrics: consumptionResults },
                });
            } catch (error) {
                Sentry.error("Failed to update local campaigns:", error);
            }
        },
        [campaignsConfigurations, dispatch],
    );

    const updateLocalCampaignsAfterEdit = useCallback(
        (updatedCampaign: IKeywordsCampaignData) => {
            const updatedCampaigns = campaignsConfigurations.map((campaign) =>
                campaign.id === updatedCampaign.id ? updatedCampaign : campaign,
            );
            dispatch({
                type: ERTContextReducerTypes.UpdateCampaignsConfigurations,
                payload: {
                    campaignsConfigurations: updatedCampaigns,
                },
            });
            const consumptionResults = getMyUserConsumptionMetrics(updatedCampaigns);

            dispatch({
                type: ERTContextReducerTypes.UpdateMyUserConsumptionMetrics,
                payload: { myUserConsumptionMetrics: consumptionResults },
            });
        },
        [campaignsConfigurations, dispatch],
    );
    const updateLocalCampaignsAfterDelete = useCallback(
        (campaignId: string) => {
            const updatedCampaigns = campaignsConfigurations.filter((campaign) => campaign.id !== campaignId);
            dispatch({
                type: ERTContextReducerTypes.UpdateCampaignsConfigurations,
                payload: {
                    campaignsConfigurations: updatedCampaigns,
                },
            });
            const consumptionResults = getMyUserConsumptionMetrics(updatedCampaigns);

            dispatch({
                type: ERTContextReducerTypes.UpdateMyUserConsumptionMetrics,
                payload: { myUserConsumptionMetrics: consumptionResults },
            });
        },
        [campaignsConfigurations, dispatch],
    );

    return {
        updateCampaignsConsumptionFromServer,
        updateLocalCampaignsAfterCreation,
        updateLocalCampaignsAfterEdit,
        updateLocalCampaignsAfterDelete,
    };
};
